import { getHash } from '~utils/product';

import { targets } from './constants';

export const onCartAction = (state, action) => {
  // Local cart before the check
  const localCart = state.cart || [];
  // Cart that responds from the backend
  const backendCart = action.payload.cart || [];
  const hasCoupon = Boolean(action.payload.coupon);

  const mappedBackendCart = backendCart.map((itemFromBackend) => {
    const backendItem = { ...itemFromBackend };
    const localItem = localCart.find((local) => {
      const localHash = local.hash || getHash(local);
      const backendHash = backendItem.hash || getHash(backendItem);
      return localHash === backendHash;
    });
    const baseItem = {
      ...backendItem,
      hash: backendItem.hash || getHash(backendItem),
      instructions: backendItem.instructions || localItem?.instructions || ''
    };
    if (localItem?.addedByCoupon) {
      return { ...baseItem, addedByCoupon: true };
    }
    if (!localItem && hasCoupon) {
      return { ...baseItem, addedByCoupon: true };
    }
    return baseItem;
  });

  // Reinsert items locally marked as addedByCoupon that did not come from the backend
  // Merge by hash to avoid duplicates
  const backendHashes = new Set(mappedBackendCart.map((b) => b.hash || getHash(b)));
  const localCouponItemsNotInBackend = (localCart || []).filter((local) => {
    const localHash = local.hash || getHash(local);
    return local.addedByCoupon && !backendHashes.has(localHash);
  });
  const mergedByHash = [...mappedBackendCart, ...localCouponItemsNotInBackend];
  const mergeMap = new Map();
  mergedByHash.forEach((it) => {
    const key = it.hash || getHash(it);
    const prev = mergeMap.get(key);
    if (prev) {
      mergeMap.set(key, { ...prev, count: (prev.count || 1) + (it.count || 1) });
    } else {
      mergeMap.set(key, it);
    }
  });
  const mergedCart = Array.from(mergeMap.values());

  return state.merge({
    [targets.CART]: mergedCart,
    [targets.FINAL_PRICE]: action.payload.total,
    [targets.SUBTOTAL_PRICE]: action.payload.subtotal,
    [targets.DELIVERY]: action.payload.delivery,
    [targets.DELIVERY_METHOD]: action.payload.deliveryMethod,
    [targets.DELIVERY_DETAILS]: action.payload.deliveryDetails,
    [targets.UBER_ESTIMATE_ID]: action.payload[targets.UBER_ESTIMATE_ID],
    [targets.UBER_ESTIMATE_EXPIRES_AT]: action.payload[targets.UBER_ESTIMATE_EXPIRES_AT],
    [targets.DISCOUNT]: action.payload.discount,
    [targets.ERROR]: '',
    [`${targets.CART}Loading`]: false,
    [`${targets.CART}Error`]: null,
    [targets.COUPON]: action.payload.coupon,
    [targets.LOYALTY_POINTS]: action.payload.loyaltyPoints,
    [targets.MAX_LOYALTY_MONEY_TO_USE]: action.payload[targets.MAX_LOYALTY_MONEY_TO_USE]
  });
};

export const onCartActionWithCoupon = (state, action) => {
  const localCart = state.cart || [];
  const backendCart = action.payload.cart || [];
  const hasCoupon = Boolean(action.payload.coupon);

  const mappedBackendCart = backendCart.map((itemFromBackend) => {
    const backendItem = { ...itemFromBackend };
    const localItem = localCart.find((local) => {
      const localHash = local.hash || getHash(local);
      const backendHash = backendItem.hash || getHash(backendItem);
      return localHash === backendHash;
    });
    const baseItem = { ...backendItem, hash: backendItem.hash || getHash(backendItem) };
    if (localItem?.addedByCoupon) {
      return { ...baseItem, addedByCoupon: true };
    }
    if (!localItem && hasCoupon) {
      return { ...baseItem, addedByCoupon: true };
    }
    return baseItem;
  });
  const backendHashes = new Set(mappedBackendCart.map((b) => b.hash || getHash(b)));
  const localCouponItemsNotInBackend = (localCart || []).filter((local) => {
    const localHash = local.hash || getHash(local);
    return local.addedByCoupon && !backendHashes.has(localHash);
  });
  const mergedByHash = [...mappedBackendCart, ...localCouponItemsNotInBackend];
  const mergeMap = new Map();
  mergedByHash.forEach((it) => {
    const key = it.hash || getHash(it);
    const prev = mergeMap.get(key);
    if (prev) {
      mergeMap.set(key, { ...prev, count: (prev.count || 1) + (it.count || 1) });
    } else {
      mergeMap.set(key, it);
    }
  });
  const mergedCart = Array.from(mergeMap.values());

  return state.merge({
    [targets.CART]: mergedCart,
    [targets.FINAL_PRICE]: action.payload.total,
    [targets.SUBTOTAL_PRICE]: action.payload.subtotal,
    [targets.DELIVERY]: action.payload.delivery,
    [targets.DELIVERY_METHOD]: action.payload.deliveryMethod,
    [targets.DELIVERY_DETAILS]: action.payload.deliveryDetails,
    [targets.UBER_ESTIMATE_ID]: action.payload[targets.UBER_ESTIMATE_ID],
    [targets.UBER_ESTIMATE_EXPIRES_AT]: action.payload[targets.UBER_ESTIMATE_EXPIRES_AT],
    [targets.DISCOUNT]: action.payload.discount,
    [targets.ERROR]: '',
    [`${targets.CART}Loading`]: false,
    [`${targets.CART}Error`]: null,
    [targets.COUPON]: action.payload.coupon,
    [targets.LOYALTY_POINTS]: action.payload.loyaltyPoints,
    [targets.MAX_LOYALTY_MONEY_TO_USE]: action.payload[targets.MAX_LOYALTY_MONEY_TO_USE]
  });
};

export const onRemoveCoupon = (state) =>
  state.merge({
    ...state,
    [targets.CART]: state[targets.CART].filter(({ addedByCoupon }) => !addedByCoupon),
    [targets.COUPON]: {
      couponData: {
        coupon: '',
        paymentMethod: ''
      },
      loyaltyData: { cashToExchange: null }
    }
  });

export const onAddItem = (state, action) => {
  const { cart: newCart } = action.payload;

  const result = state.merge({
    [targets.CART]: newCart
  });

  return result;
};

export const onSumItem = (state, action) => {
  const { cart: newCart } = action.payload;
  return state.merge({
    [targets.CART]: newCart
  });
};

export const onModifyItem = (state, action) => {
  const { cart: newCart } = action.payload;
  return state.merge({
    [targets.CART]: newCart
  });
};

export const onRemoveItem = (state, action) => {
  const { cart: newCart } = action.payload;

  const result = state.merge({
    [targets.CART]: newCart
  });

  return result;
};
